import type { ReactNode } from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Badge } from '@/components/lemmala-design-system/badge/badge'

import { headerContentVariants } from './variants'

export type MainPageHeaderProps = {
  title: ReactNode
  subtitle?: ReactNode
  badge?: ReactNode
  actions?: ReactNode
  tabs?: ReactNode
  contentWidth?: 'full' | 'contained' | 'wide'
  className?: string
}

export const MainPageHeader = ({
  title,
  subtitle,
  badge,
  actions,
  tabs,
  contentWidth = 'full',
  className,
}: MainPageHeaderProps) => {
  return (
    <header
      className={cn('flex w-full flex-col bg-main-background-canvas', className)}
      data-testid='main-page-header'
    >
      <div className='flex h-24 items-center px-11' data-testid='main-page-header-band'>
        <div
          className={cn(headerContentVariants({ contentWidth }), 'flex min-h-8 items-center gap-8')}
        >
          <div className='flex min-w-0 flex-1 flex-col justify-center [contain:inline-size]'>
            <div className='flex min-w-0 items-center gap-2'>
              <h1 className='truncate text-main-foreground-primary text-heading-large'>{title}</h1>
              {badge != null && (
                <Badge variant='neutral' badgeStyle='subtle' size='large'>
                  {badge}
                </Badge>
              )}
            </div>
            {subtitle && (
              <p
                className='truncate text-main-foreground-primary text-body-default'
                data-testid='main-page-header-subtitle'
              >
                {subtitle}
              </p>
            )}
          </div>
          {actions && (
            <div
              className='flex shrink-0 items-center gap-6'
              data-testid='main-page-header-actions'
            >
              {actions}
            </div>
          )}
        </div>
      </div>
      {tabs != null && (
        <div
          className='flex h-10 items-center border-b border-main-border-default px-11'
          data-testid='main-page-header-tabs'
        >
          <div className={cn(headerContentVariants({ contentWidth }), 'flex h-full items-center')}>
            {tabs}
          </div>
        </div>
      )}
    </header>
  )
}
